import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ruler, Tag, Palette, AlertCircle, ShieldCheck, Scale } from "lucide-react";

function Reveal({ children, delay = 0, className = "", ...rest }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <Reveal className="border-t border-neutral-200 pt-10" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-teal-600 shrink-0">
          <Icon size={18} strokeWidth={2} />
        </div>
        <h2 className="font-heading font-extrabold text-2xl text-neutral-900 tracking-tight">{title}</h2>
      </div>
      <div className="space-y-3 text-neutral-600 leading-relaxed pl-0 sm:pl-13">{children}</div>
    </Reveal>
  );
}

export default function Returns() {
  return (
    <div data-testid="returns-page">
      {/* HERO */}
      <section className="bg-charcoal text-white relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1 bg-[#09B8DF]" />
        <div className="container-lookers py-20 sm:py-24 relative z-10 max-w-3xl">
          <Reveal>
            <div className="text-[#09B8DF] text-xs font-heading font-bold uppercase tracking-[0.25em] mb-5">Returns &amp; Refunds</div>
            <h1 className="font-heading font-extrabold text-5xl sm:text-6xl tracking-tight mb-6">Know exactly where you stand.</h1>
            <p className="text-lg text-white/70 leading-relaxed mb-4">
              Every Lookers frame is individually photographed, measured and condition graded so you can make an informed
              decision before buying. Because most of our frames are unique preloved pieces, we generally don't accept
              returns simply because you've changed your mind.
            </p>
            <p className="text-white/70 leading-relaxed">
              If there's a genuine problem with your purchase, we'll work with you in accordance with your rights under
              Australian Consumer Law.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="container-lookers py-16 sm:py-20 max-w-3xl space-y-12">
        {/* CHANGE OF MIND */}
        <Reveal data-testid="returns-change-of-mind">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-teal-600 shrink-0"><Scale size={18} /></div>
            <h2 className="font-heading font-extrabold text-2xl text-neutral-900 tracking-tight">Changed your mind?</h2>
          </div>
          <p className="text-neutral-600 leading-relaxed mb-4">
            Because Lookers specialises in individual preloved frames, we generally don't offer refunds or exchanges for change of mind.
          </p>
          <p className="text-neutral-600 leading-relaxed mb-3">This includes situations where:</p>
          <ul className="list-disc list-inside space-y-1.5 text-neutral-600 mb-6">
            <li>you decide you no longer want the frame</li>
            <li>you find another frame you prefer</li>
            <li>you find the item cheaper elsewhere</li>
            <li>the frame does not suit your personal style</li>
            <li>you selected an unsuitable size despite accurate measurements being provided before purchase</li>
          </ul>
          <div className="bg-teal-50 border border-teal-100 rounded-2xl p-5 text-sm text-teal-900 font-heading font-semibold">
            This change-of-mind policy does not limit any rights or remedies available to you under Australian Consumer Law.
          </div>
        </Reveal>

        <Section icon={Ruler} title="Please check your measurements." testid="returns-sizing">
          <p>
            Eyewear sizing matters. Before purchasing, compare the measurements shown on the product page with a pair of
            glasses that already fits you comfortably.
          </p>
          <p>Lookers provides frame measurements wherever available to help you make an informed choice.</p>
          <Link to="/condition-guide" className="inline-flex items-center gap-1.5 mt-2 text-xs font-heading font-bold uppercase tracking-wide text-teal-700 hover:text-teal-800 transition-colors" data-testid="returns-size-guide-link">
            Read Our Frame Size Guide
          </Link>
          <p className="text-sm text-neutral-500">
            If the supplied measurements are materially incorrect, that isn't treated as change of mind — please contact us so we can assess it.
          </p>
        </Section>

        <Section icon={Tag} title="Preloved means preloved." testid="returns-preloved">
          <p>Our frames have had a life before Lookers, and signs of previous wear can be part of that story.</p>
          <p>
            We aim to show and describe relevant wear before purchase through our Condition Rating, written condition notes and photographs.
          </p>
          <p>
            Wear or characteristics clearly disclosed before purchase aren't treated as undisclosed defects simply because the item is preloved.
            This does not affect your rights if a separate, undisclosed problem arises.
          </p>
        </Section>

        <Section icon={Palette} title="A note about colour." testid="returns-colour">
          <p>
            We aim to represent every frame as accurately as possible. Colours can appear slightly different depending on
            photography, lighting and your screen or device settings.
          </p>
        </Section>

        <Section icon={AlertCircle} title="Something isn't right?" testid="returns-problem">
          <p>
            If your frame arrives damaged, materially differs from its description, or you believe there's another problem with
            your purchase, contact Lookers as soon as reasonably possible.
          </p>
          <p>When you get in touch, it helps to have:</p>
          <ul className="list-disc list-inside space-y-1.5 text-neutral-600">
            <li>your order number</li>
            <li>a description of the issue</li>
            <li>relevant photographs where useful</li>
          </ul>
          <Link to="/account/orders" className="inline-flex items-center gap-2 mt-3 bg-charcoal hover:bg-teal-600 text-white font-heading font-bold text-xs uppercase tracking-wider px-6 py-3.5 rounded-full transition-colors" data-testid="returns-get-help-cta">
            Get Help With An Order
          </Link>
        </Section>

        <Section icon={ShieldCheck} title="Concerned about authenticity?" testid="returns-authenticity">
          <p>
            Authenticity matters to us. If you have a genuine concern about a frame purchased from Lookers, contact us with
            your order number and the reason for your concern.
          </p>
          <p>
            We'll review the frame's Lookers record and the information available and may request photographs or arrange further
            assessment where appropriate.
          </p>
          <Link to="/authenticity" className="inline-flex items-center gap-1.5 mt-2 text-xs font-heading font-bold uppercase tracking-wide text-teal-700 hover:text-teal-800 transition-colors" data-testid="returns-authenticity-link">
            Read the Lookers Authenticity Guarantee
          </Link>
        </Section>

        {/* ACL */}
        <Reveal className="bg-charcoal text-white rounded-3xl p-8 relative overflow-hidden" data-testid="returns-acl">
          <div className="absolute inset-x-0 top-0 h-1 bg-[#09B8DF]" />
          <div className="text-[#09B8DF] text-xs font-heading font-bold uppercase tracking-[0.2em] mb-3">Your Statutory Rights</div>
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight mb-4">Australian Consumer Law</h2>
          <div className="space-y-3 text-white/70 leading-relaxed">
            <p>
              Our goods come with guarantees that cannot be excluded under the Australian Consumer Law. You are entitled to a
              replacement or refund for a major failure and to compensation for any other reasonably foreseeable loss or damage.
            </p>
            <p>
              You are also entitled to have goods repaired or replaced if the goods fail to be of acceptable quality and the
              failure does not amount to a major failure.
            </p>
            <p>
              Nothing in Lookers' voluntary returns policy excludes, restricts or modifies any rights or remedies you have under
              the Australian Consumer Law. Our change-of-mind policy never applies where you are seeking a remedy for a product
              that fails an applicable consumer guarantee.
            </p>
            <p className="text-sm text-white/50">
              This page is a plain-English summary of our returns approach and is not legal advice.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
